import React from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import TextField from '@mui/material/TextField';
import Alert from '@mui/material/Alert';
import MenuItem from '@mui/material/MenuItem';
import CircularProgress from '@mui/material/CircularProgress';
import LightbulbIcon from '@mui/icons-material/LightbulbOutlined';
import useVoyagerStore from '../../stores/useVoyagerStore';
import type { LandingAgentTurn } from '../../stores/slices/landingAgentSlice';
import { followUpOf } from '../../stores/slices/landingAgentSlice';
import BuildingBlocks, { blocksToPreview } from './DefinitionBuildingBlocks';
import type { BlockState } from './DefinitionBuildingBlocks';
import ProposedDefinitionCard from './ProposedDefinitionCard';
import { definitionCaptureContext } from '../../apis';
import { stromaAnswerCard, stromaInset, stromaSectionLabel } from './askPresentation';

type Mode = 'english' | 'blocks' | 'sql';

/** #1665: the question with the term's words replaced by a meaning's name — what a chosen meaning re-asks, so the
 *  re-ask shows its literal words (the server's `term_check.reasked`, mirrored). */
export function reaskedQuestion(question: string, term: string, label: string): string {
  const words = (term.match(/\w+/g) || []).map((w) => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  if (!words.length) return question;
  const pattern = new RegExp(`\\b${words.join('\\W+')}\\b`, 'i');
  return pattern.test(question) ? question.replace(pattern, () => label) : `${question} (${label})`;
}

/** #1665: the question back as one line — words as written, a cited term in quotes. */
export function questionText(parts: { text?: string; kind?: string; label?: string }[] | undefined): string {
  return (parts || []).map((p) => (typeof p.text === 'string' ? p.text
    : typeof p.label === 'string' ? (p.kind === 'term' ? `“${p.label}”` : p.label) : ''))
    .map((t) => t.trim()).filter(Boolean).join(' ');
}

const KIND_LABEL: Record<string, string> = {
  metric: 'Metric',
  dimension: 'Dimension',
  term: 'Business term',
  cohort: 'Cohort / filter',
};

/**
 * The moment the semantic layer admits it doesn't know a word, and asks.
 *
 * Three input modes because people arrive with different handles on the same
 * idea: prose, fields, or the expression they already had in mind. All three
 * end at the same place — a formula, a dry run, and numbers on screen before
 * anything is written.
 */
export default function DefinitionCaptureCard({ turn }: { turn: LandingAgentTurn }) {
  const request = turn.definitionRequest;
  const proposal = useVoyagerStore((s) => s.definitionProposals[turn.id]);
  const proposing = useVoyagerStore((s) => !!s.definitionProposing[turn.id]);
  const captureError = useVoyagerStore((s) => s.definitionCaptureError[turn.id]);
  const saved = useVoyagerStore((s) => s.definitionSaved[turn.id]);
  const requestMessage = useVoyagerStore((s) => s.definitionRequestMessage[turn.id]);
  const requestTurnDefinition = useVoyagerStore((s) => s.requestTurnDefinition);
  const proposeTurnDefinition = useVoyagerStore((s) => s.proposeTurnDefinition);
  const sendLandingAgentQuery = useVoyagerStore((s) => s.sendLandingAgentQuery);
  const seedTurnProposal = useVoyagerStore((s) => s.seedTurnProposal);

  const [context, setContext] = React.useState(request);
  // #1665: under Auto the asker chooses where the draft belongs; the card then continues as it always has
  const [chosen, setChosen] = React.useState<string>('');
  const [mode, setMode] = React.useState<Mode>('english');
  const [kind, setKind] = React.useState<string>(request?.kind_hint || 'metric');
  const [english, setEnglish] = React.useState('');
  const [sql, setSql] = React.useState('');
  const [blocks, setBlocks] = React.useState<BlockState>({
    measure: 'COUNT DISTINCT',
    field: '',
    conditions: [{ field: '', op: 'within last', value: '30 days' }],
  });
  // What the agent proposed, kept so an edited formula can be told apart from
  // the one the English describes.
  const [proposedFormula, setProposedFormula] = React.useState('');

  // Seed the SQL tab from the proposal. "Edit definition" usually means "the
  // formula is nearly right" — the assumptions note literally says things like
  // "adjust to >= 500" — and landing on an empty box means retyping the whole
  // expression to change one character.
  const candidateFormula = proposal?.candidate?.formula || '';
  React.useEffect(() => {
    if (!candidateFormula) return;
    setSql(candidateFormula);
    setProposedFormula(candidateFormula);
  }, [candidateFormula]);

  // #1670: every perspective name the card shows follows the active candidate (an O2 choice replaces it), never the
  // request alone
  const perspective = proposal?.candidate?.perspective || request?.perspective || chosen;
  const perspectiveName = (perspective === request?.perspective ? request?.perspective_name : null)
    || request?.alternatives?.find((o) => o.id === perspective)?.name
    || (request?.placement?.perspective === perspective ? request?.placement?.perspective_name : null)
    || request?.perspective_options?.find((o) => o.id === perspective)?.name || perspective;
  React.useEffect(() => {
    if (!request || !perspective) return;
    let active = true;
    const timer = setTimeout(() => {
      definitionCaptureContext(`${request.term} ${english} ${sql}`, perspective)
        .then((data) => { if (active) setContext(data); }).catch(() => {});
    }, 300);
    return () => { active = false; clearTimeout(timer); };
  }, [request, perspective, english, sql]);

  // #1665: a reply the server already drafted and ran is this turn's proposal
  React.useEffect(() => {
    if (request?.proposal?.candidate) seedTurnProposal(turn.id, request.proposal);
  }, [request, turn.id, seedTurnProposal]);

  if (!request) return null;
  const term = request.term || 'that term';
  // the question the term was asked in: a reply's card answers the original question once saved
  const askedQuestion = request.asked_question || turn.userText;
  const choosePerspective = (id: string) => {
    setChosen(id);
    // the meaning was already given (#1665 §4): drafted and run there at once — nothing is saved
    if (request.english) {
      proposeTurnDefinition(turn.id, {
        term, perspective: id, kind: request.kind_hint || 'metric', english: request.english,
        breakdown: request.breakdown || undefined,
        provenance: { question: askedQuestion, thread_turn: turn.id, session_id: turn.sessionId || '' },
      });
    }
  };
  const asked = questionText(request.question);
  const meanings = request.meanings || [];
  // a meaning re-sends this turn's own request with the term's words replaced (#760's Change does the same)
  const askMeaning = (label: string) => {
    const reask = turn.askedRequest;
    if (reask) {
      const query = reaskedQuestion(reask.query, term, label);
      sendLandingAgentQuery(query, { reask: { ...reask, query } });
    } else {
      sendLandingAgentQuery(reaskedQuestion(turn.userText, term, label), followUpOf(turn, { sameQuestion: true }));
    }
  };
  const termAsk = !!request.term_check && (asked || meanings.length > 0);
  const header = termAsk ? (
    <Box sx={{ mb: 1.5 }} data-testid="term-check-question">
      {asked && <Typography variant="body2" sx={{ mb: meanings.length ? 1 : 0 }}>{asked}</Typography>}
      {request.english && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Your meaning: “{request.english}”
        </Typography>
      )}
      {request.error && <Alert severity="warning" sx={{ mb: 1 }}>{request.error}</Alert>}
      {meanings.length > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
          {meanings.map((m) => (
            <Chip key={m.id} size="small" color="primary" variant="outlined" label={m.label}
                  onClick={() => askMeaning(m.label)} />
          ))}
        </Box>
      )}
    </Box>
  ) : null;
  const where = !perspective && (request.perspective_options || []).length > 0 ? (
    <TextField select fullWidth size="small" label="Which perspective should it belong to?" value={chosen}
               onChange={(e) => choosePerspective(e.target.value)} sx={{ mb: 1.5 }}
               SelectProps={{ MenuProps: { PaperProps: { sx: { maxHeight: 320 } } } }}>
      {(request.perspective_options || []).map((o) => <MenuItem key={o.id} value={o.id}>{o.name}</MenuItem>)}
    </TextField>
  ) : null;
  const askDataTeam = () => requestTurnDefinition(turn.id, {
    term, perspective, kind, english, sql,
    provenance: { question: turn.userText, thread_turn: turn.id,
      session_id: turn.sessionId || '', page_url: window.location.href,
      page_title: document.title, attempted_blocks: mode === 'blocks' ? blocks : undefined },
  });
  if (saved?.status === 'requested') {
    return <Alert severity="success">{requestMessage ||
      'Question sent to the data team in the Definitions queue. It is awaiting an answer.'}</Alert>;
  }
  // #1670: "Draft it in another perspective" — the same meaning re-proposed there (a new draft and dry run, nothing
  // saved); the propose route loads the perspective as the asker
  const alternatives = request.alternatives || [];
  const draftElsewhere = (id: string) => proposeTurnDefinition(turn.id, {
    term, perspective: id, kind: request.kind_hint || 'metric', english: request.english || undefined,
    breakdown: request.breakdown || undefined,
    provenance: { question: askedQuestion, thread_turn: turn.id, session_id: turn.sessionId || '' },
  });
  const elsewhere = alternatives.length > 0 && !saved ? (
    <TextField select fullWidth size="small" label="Draft it in another perspective" value=""
               onChange={(e) => draftElsewhere(e.target.value)} sx={{ mb: 1.5 }} disabled={proposing}
               SelectProps={{ MenuProps: { PaperProps: { sx: { maxHeight: 320 } } } }}>
      {alternatives.map((o) => <MenuItem key={o.id} value={o.id}>{o.name}</MenuItem>)}
    </TextField>
  ) : null;
  // the placement line, only while the turn's proposal is the server's (drafted in the placement's home)
  const placed = !!request.placement && proposal?.candidate?.perspective === request.placement.perspective;
  if (saved || proposal) {
    return (
      <>
        {placed && asked && (
          <Typography variant="body2" sx={{ mb: 1 }} data-testid="term-check-placement">{asked}</Typography>
        )}
        {elsewhere}
        <ProposedDefinitionCard
          turn={turn}
          term={request.term}
          perspectiveName={perspectiveName || ''}
          question={askedQuestion}
        />
        {!saved && <Button size="small" disabled={proposing} onClick={askDataTeam}>
          I don’t know — ask the data team
        </Button>}
      </>
    );
  }

  const nearby = request.nearby || [];
  // Somebody already wrote this one and it hasn't been reviewed yet. Only
  // `draft` gets the run offer: `needs_changes` was taken offline by a
  // reviewer on purpose, and the server refuses to resolve it either.
  const drafts = request.pending_drafts || [];
  const canSubmit = mode === 'english'
    ? english.trim().length > 3
    : mode === 'sql'
      ? sql.trim().length > 3
      : blocks.field.trim().length > 0;

  const submit = () => {
    if (!canSubmit || proposing) return;
    proposeTurnDefinition(turn.id, {
      term,
      perspective,
      kind,
      // English rides along in SQL mode too. Without it the server derives a
      // placeholder from the expression ("Whales: COUNT(...)"), throwing away
      // the human sentence — which is the part a reviewer actually reads.
      english: mode === 'blocks' ? undefined : english.trim() || undefined,
      blocks: mode === 'blocks' ? blocks : undefined,
      sql: mode === 'sql' ? sql.trim() : undefined,
      // So the preview groups by what the question asked for.
      breakdown: request.breakdown || undefined,
      provenance: {
        question: askedQuestion,
        thread_turn: turn.id,
        session_id: turn.sessionId || '',
      },
    });
  };

  // #1665: under Auto, nothing to define against until the asker chooses where it belongs
  if (!perspective) {
    return (
      <Paper variant="outlined" sx={{ p: { xs: 2, sm: 2.5 }, ...stromaAnswerCard }} data-testid="definition-capture-card">
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25, mb: 1.5 }}>
          <LightbulbIcon fontSize="small" sx={{ color: 'warning.main', mt: '2px' }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 700, fontFamily: 'var(--display)', fontSize: 16, letterSpacing: '-0.01em', textTransform: 'none' }}>
            “{term}” isn’t defined yet
          </Typography>
        </Box>
        {header}
        {where}
        {proposing && <CircularProgress size={16} />}
        {captureError && <Alert severity="error" sx={{ mt: 1.5 }}>{captureError}</Alert>}
      </Paper>
    );
  }

  return (
    <Paper variant="outlined" sx={{ p: { xs: 2, sm: 2.5 }, ...stromaAnswerCard }} data-testid="definition-capture-card">
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25, mb: 1.5 }}>
        <LightbulbIcon fontSize="small" sx={{ color: 'warning.main', mt: '2px' }} />
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, fontFamily: 'var(--display)', fontSize: 16, letterSpacing: '-0.01em', textTransform: 'none' }}>
            “{term}” isn’t defined yet
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {perspectiveName} has no metric, dimension,
            or term matching “{term}”. Define it once and every future question can use it.
          </Typography>
        </Box>
      </Box>
      {header}
      {elsewhere}

      {/* Before the form, not after it: someone has already written this
          definition, and finding that out only when the save collides means
          the work was wasted. */}
      {drafts.map((d) => (
        <Alert
          key={d.id}
          severity={d.status === 'needs_changes' ? 'warning' : 'info'}
          sx={{ mb: 1.5 }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {d.author ? `${d.author} has already defined this` : 'Someone has already defined this'}
            {d.status === 'needs_changes' ? ' — sent back for changes' : ' — waiting on review'}
          </Typography>
          {d.english && (
            <Typography variant="body2" sx={{ fontStyle: 'italic', mt: 0.5 }}>
              “{d.english}”
            </Typography>
          )}
          {d.formula && (
            <Typography component="pre" variant="caption"
                        sx={{ fontFamily: 'monospace', m: 0, mt: 0.5,
                              whiteSpace: 'pre-wrap', opacity: 0.8 }}>
              {d.formula}
            </Typography>
          )}
          {d.status === 'needs_changes' ? (
            <Typography variant="caption" color="text.secondary"
                        sx={{ display: 'block', mt: 0.75 }}>
              A reviewer sent it back{d.review_note ? `: “${d.review_note}”` : ''}, so it
              can’t answer questions until it’s revised. Define your own below if you need
              an answer now.
            </Typography>
          ) : (
            <Box sx={{ mt: 1 }}>
              <Button
                size="small"
                variant="outlined"
                // The server requires the revision, so a card without one
                // would offer a button that quietly returns this same card.
                // An old tab is the only way to get here.
                disabled={!d.version}
                onClick={() => sendLandingAgentQuery(turn.userText, {
                  ...followUpOf(turn, { sameQuestion: true }),
                  // The revision shown above, not just the id: if the author
                  // revises it between this card rendering and this click,
                  // the server refuses rather than running wording nobody read.
                  useDefinitions: [`${d.id}@${d.version}`],
                })}
              >
                See results using this draft
              </Button>
              <Typography variant="caption" color="text.secondary"
                          sx={{ display: 'block', mt: 0.75 }}>
                {d.version
                  ? 'Just this once — the answer will say it came from an unreviewed '
                    + 'draft, and the draft still won’t apply to your other questions '
                    + 'until it’s published. Or write your own below.'
                  : 'This card is out of date — ask the question again to run it. '
                    + 'Or write your own definition below.'}
              </Typography>
            </Box>
          )}
        </Alert>
      ))}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
        <Typography variant="caption" sx={stromaSectionLabel} color="text.secondary">Save as</Typography>
        {(request.kinds || Object.keys(KIND_LABEL)).map((k) => (
          <Chip
            key={k}
            size="small"
            label={KIND_LABEL[k] || k}
            variant={kind === k ? 'filled' : 'outlined'}
            color={kind === k ? 'primary' : 'default'}
            onClick={() => setKind(k)}
          />
        ))}
        {request.kind_hint && (
          <Typography variant="caption" color="text.secondary">
            · {KIND_LABEL[request.kind_hint] || request.kind_hint} suggested
          </Typography>
        )}
      </Box>

      <Box sx={{ mb: 1.5 }}>
        <Typography variant="subtitle2" sx={stromaSectionLabel}>Fields you can use in {perspectiveName}</Typography>
        {(context?.field_groups || request.field_groups || []).map((group) => (
          <Typography key={group.name} variant="body2" color="text.secondary">
            {group.name}: {group.fields.map((field) => field.name).join(', ')}
          </Typography>
        ))}
        {(context?.field_locations || request.field_locations || []).map((location) => (
          <Alert key={`${location.perspective}:${location.field}`} severity="info" sx={{ mt: 1 }}>
            {location.perspective_name} has {location.field}.
          </Alert>
        ))}
      </Box>

      <Tabs
        value={mode === 'sql' ? 'english' : mode}
        onChange={(_, v) => setMode(v)}
        sx={{ minHeight: 36, mb: 1.5, '& .MuiTab-root': { minHeight: 36, fontSize: 12 } }}
      >
        <Tab value="english" label="Plain English" />
        <Tab value="blocks" label="Building blocks" />
      </Tabs>

      {mode === 'english' && (
        <>
          <TextField
            fullWidth
            multiline
            minRows={3}
            size="small"
            autoFocus
            label="In plain English"
            placeholder="A consumer counts as new when their first order was in the last 30 days."
            value={english}
            onChange={(e) => setEnglish(e.target.value)}
          />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: 'block' }}>
            The agent translates this into a formula, validates it against the warehouse,
            and shows you the numbers before anything is saved.
          </Typography>
        </>
      )}

      {mode === 'blocks' && (
        <>
          <BuildingBlocks
            fieldGroups={request.field_groups || []}
            blocks={blocks}
            onChange={setBlocks}
          />
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: 'block' }}>
            Only fields relevant to “{term}” are suggested up front; search covers everything
            in {perspectiveName}.
          </Typography>
        </>
      )}

      <details open={mode === 'sql'}>
        <summary onClick={(event) => {
          event.preventDefault();
          setMode(mode === 'sql' ? 'english' : 'sql');
        }}>I'd rather write it myself</summary>
        <>
          <TextField
            fullWidth
            multiline
            minRows={3}
            size="small"
            value={sql}
            onChange={(e) => setSql(e.target.value)}
            inputProps={{ style: { fontFamily: 'monospace', fontSize: 12 } }}
          />
          {/* Reviewers judge a definition by its English, so prose that no
              longer matches the formula is worse than no prose. Say it plainly
              rather than letting the two drift apart in the saved row. */}
          {!!proposedFormula && sql.trim() !== proposedFormula.trim() && english.trim() && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              You’ve changed the formula, but the plain-English description still
              says: “{english.trim()}”. Update it on the Plain English tab —
              that sentence is what a reviewer reads.
            </Alert>
          )}
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: 'block' }}>
            An aggregate expression against {perspectiveName} fields —
            validated with a dry run before saving. No SELECT, no FROM.
          </Typography>
        </>
      </details>

      {nearby.length > 0 && (
        <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
          <LightbulbIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
          <Typography variant="caption" color="text.secondary">Nearby definitions:</Typography>
          {nearby.slice(0, 3).map((n) => (
            <Chip
              key={n.name}
              size="small"
              variant="outlined"
              label={n.display_name || n.name}
              title={n.description || ''}
              onClick={() => sendLandingAgentQuery(
                `${turn.userText}\n\nTo clarify: use the existing metric ${n.name}.`, followUpOf(turn, { sameQuestion: true }),
              )}
            />
          ))}
          <Typography variant="caption" color="text.secondary">
            — reuse one if it already means “{term}”.
          </Typography>
        </Box>
      )}

      {captureError && (
        <Alert severity="error" sx={{ mt: 1.5 }}>{captureError}</Alert>
      )}

      {mode === 'blocks' && blocks.field && (
        <Box sx={{ mt: 1.5, p: 1, ...stromaInset }}>
          <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
            {blocksToPreview(blocks)}
          </Typography>
        </Box>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 2, flexWrap: 'wrap' }}>
        <Button
          variant="contained"
          size="small"
          disabled={!canSubmit || proposing}
          onClick={submit}
          startIcon={proposing ? <CircularProgress size={14} color="inherit" /> : undefined}
        >
          {proposing ? 'Validating…' : 'Validate & preview'}
        </Button>
        <Button
          size="small"
          disabled={proposing}
          onClick={() => sendLandingAgentQuery(
            `${turn.userText}\n\nAnswer without “${term}” — use the closest metric this perspective already has.`,
            followUpOf(turn, { sameQuestion: true }),
          )}
        >
          Answer without it
        </Button>
        <Button size="small" disabled={proposing} onClick={askDataTeam}>
          I don’t know — ask the data team
        </Button>
      </Box>
    </Paper>
  );
}
